import { RefObject, useCallback } from "react";
import useDataKey from "./useDataKey";
import { BarCharts, Filtro, Graphics } from "../interfaces/Indicator";
import { INDICATOR_TYPE_PROCESS, INDICATOR_TYPE_RESULTS } from "../utils/const";
import { sendAnalyticsEvent } from "../FirebaseConfig";
import { DataKey, useDataKeyDualAxis } from "./useDataKeyDualAxis";

// Constantes al inicio del archivo o en un archivo separado
const CANVAS_CONFIG = {
    legendHeight: 30,
    padding: 20,
    margin: 20,
    lineHeight: 20,
    legendItemSpacing: 25,
    legendRectSize: 16,
    legendRectMargin: 5,
} as const;

const FONTS = {
    normal: "14px Roboto",
    bold: "bold 14px Roboto",
    title: "bold 20px Roboto",
} as const;

const COLORS = {
    black: "#000",
    gray: "#666",
    white: "#FFFFFF",
} as const;

//Calcular la altura del canva
export const calculateCanvasHeight = (
    breadcrumb: string = '',
    filtersSelected: { [key: string]: string[] } = {},
    dataKey: any[] = [],
    typeOfIndicator: string = '',
    svgHeight: number = 300,
    ctx: CanvasRenderingContext2D,
    margin: number = 20,
    notes: string = "",
    legendHeight: number = 40,
    source: string = ""
) => {
    let totalHeight = margin; // Altura inicial (margen superior)

    // Calcular altura del breadcrumb
    totalHeight += 20 * Math.ceil(breadcrumb.length / 60);

    // Calcular altura del título
    totalHeight += 30;

    // Calcular altura de los filtros seleccionados
    if (filtersSelected && Object.keys(filtersSelected).length > 0) {
        totalHeight += 20; // Espacio para el header de filtros
        totalHeight += Object.keys(filtersSelected).length * 20; // Cada línea de filtro
        totalHeight += 10; // Espacio adicional después de los filtros
    }

    // Añadir la altura del gráfico SVG
    totalHeight += svgHeight;

    if (dataKey) {
        // Calcular altura de la leyenda
        const maxWidth = ctx.canvas.width - margin * 2 - 40;
        let currentLineWidth = 0;
        let legendLines = 1;

        const legendItems =
            typeOfIndicator === INDICATOR_TYPE_PROCESS
                ? dataKey?.filter((item) =>
                    item.key.toLowerCase().includes("variación")
                )
                : dataKey;

        legendItems?.forEach((item) => {
            const textWidth = ctx.measureText(item.key).width;
            const legendItemWidth = 16 + 5 + textWidth + 25;

            if (currentLineWidth + legendItemWidth > maxWidth) {
                currentLineWidth = 0;
                legendLines++; // Salto a la siguiente línea
            }
            currentLineWidth += legendItemWidth;
        });

        totalHeight += legendLines * 25 + legendHeight;
    }

    // Calcular altura de la nota y la fuente
    const lineHeight = 18;

    if (notes) {
        const noteLines = Math.ceil(notes.length / 60);
        totalHeight += 20 + noteLines * lineHeight;
    }

    if (source) {
        const sourceLines = Math.ceil(source.length / 60);
        totalHeight += 20 + sourceLines * lineHeight;
    }

    // Añadir margen inferior
    totalHeight += margin;

    return totalHeight;
};

// Función para dividir el texto en líneas, si es necesario
// Función mejorada para dividir el texto en líneas y devolver la altura utilizada
export const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number
): number => {
    const words = text.split(" ");
    let line = "";
    let lineY = y;
    let lineCount = 0;

    for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + " ";
        const testWidth = ctx.measureText(testLine).width;

        if (testWidth > maxWidth && i > 0) {
            ctx.fillText(line, x, lineY);
            line = words[i] + " ";
            lineY += lineHeight;
            lineCount++;
        } else {
            line = testLine;
        }
    }
    ctx.fillText(line, x, lineY);
    lineCount++;

    return lineCount * lineHeight; // Devuelve la altura total del texto
};

// Función para dibujar items de leyenda
const drawLegendItems = (
    ctx: CanvasRenderingContext2D,
    items: DataKey[],
    startX: number,
    startY: number,
    maxWidth: number
): number => {
    let currentLineWidth = 0;
    let currentHeight = startY;

    items.forEach((item) => {
        const textWidth = ctx.measureText(item.key).width;
        const legendItemWidth =
            CANVAS_CONFIG.legendRectSize +
            CANVAS_CONFIG.legendRectMargin +
            textWidth +
            CANVAS_CONFIG.legendItemSpacing;

        if (currentLineWidth + legendItemWidth > maxWidth) {
            currentLineWidth = 0;
            currentHeight += CANVAS_CONFIG.legendItemSpacing;
        }

        ctx.fillStyle = item.color;
        ctx.fillRect(
            startX + currentLineWidth,
            currentHeight,
            CANVAS_CONFIG.legendRectSize,
            CANVAS_CONFIG.legendRectSize
        );
        ctx.fillText(
            item.key,
            startX + currentLineWidth + CANVAS_CONFIG.legendItemSpacing,
            currentHeight + 12
        );

        currentLineWidth += legendItemWidth;
    });

    return currentHeight;
};

// Función para dibujar breadcrumb y título
const drawHeader = (
    ctx: CanvasRenderingContext2D,
    breadcrumbs: string,
    nameIndicator: string,
    chartTitle: string,
    canvasWidth: number,
    startY: number
): number => {
    const margin = CANVAS_CONFIG.margin;
    const lineHeight = CANVAS_CONFIG.lineHeight;
    let contentHeight = startY;

    // Breadcrumb
    ctx.font = FONTS.normal;
    ctx.fillStyle = COLORS.black;
    ctx.textAlign = "left";

    const breadcrumbHeight = wrapText(
        ctx,
        `${breadcrumbs.replaceAll(">", " / ")} / ${nameIndicator}`,
        margin,
        contentHeight,
        canvasWidth - margin * 2,
        lineHeight
    );
    contentHeight += breadcrumbHeight + 10;

    // Título
    ctx.font = FONTS.title;
    const titleHeight = wrapText(
        ctx,
        chartTitle,
        margin,
        contentHeight,
        canvasWidth - margin * 2,
        lineHeight
    );
    contentHeight += titleHeight + 10;

    return contentHeight;
};

// Función para dibujar filtros
const drawFilters = (
    ctx: CanvasRenderingContext2D,
    filtersSelected: { [key: string]: string[] },
    startX: number,
    startY: number
): number => {
    if (!filtersSelected || Object.keys(filtersSelected).length === 0) {
        return startY;
    }

    let contentHeight = startY;
    const filtersTextLines = Object.entries(filtersSelected).map(
        ([key, value]) => `${key}: ${value.filter((v) => v !== 'Todos').join(', ')}`
    );

    ctx.font = FONTS.normal;
    ctx.fillStyle = COLORS.black;
    ctx.fillText("Filtros seleccionados:", startX, contentHeight);
    contentHeight += 20;

    filtersTextLines.forEach((line) => {
        ctx.fillStyle = COLORS.gray;
        ctx.fillText(line, startX, contentHeight);
        contentHeight += 20;
    });

    return contentHeight + 10;
};

// Función para dibujar notas y fuente
const drawFooter = (
    ctx: CanvasRenderingContext2D,
    note: string | undefined,
    source: string,
    startX: number,
    startY: number,
    maxWidth: number
): number => {
    let contentHeight = startY;
    const lineHeight = CANVAS_CONFIG.lineHeight;

    ctx.font = FONTS.normal;
    ctx.fillStyle = COLORS.black;

    if (note && note.length > 0) {
        wrapText(ctx, `Nota: ${note}`, startX, contentHeight, maxWidth, lineHeight);
        contentHeight += 20 + lineHeight * Math.ceil(note.length / 60);
    }

    if (source && source.length > 0) {
        wrapText(ctx, `Fuente: ${source}`, startX, contentHeight, maxWidth, lineHeight);
        contentHeight += 20 + lineHeight;
    }

    return contentHeight;
};

// Función para obtener items de leyenda filtrados
const getLegendItems = (
    items: DataKey[],
    typeOfIndicator: string,
    filterByVariation: boolean = false
): DataKey[] => {
    if (typeOfIndicator === INDICATOR_TYPE_PROCESS && filterByVariation) {
        return items.filter((item) => item.key.toLowerCase().includes('variación'));
    }
    return items;
};

const drawLegend = (
    ctx: CanvasRenderingContext2D,
    hasSecondaryAxis: boolean,
    dualAxisData: ReturnType<typeof useDataKeyDualAxis>,
    singleAxisData: ReturnType<typeof useDataKey>,
    typeOfIndicator: string,
    margin: number,
    startY: number,
    maxWidth: number
): number => {
    let contentHeight = startY;

    if (hasSecondaryAxis && typeOfIndicator === INDICATOR_TYPE_RESULTS) {
        //Eje izquierdo
        const leftItems = dualAxisData.dataKey.left;
        // Eje derecho
        const rightItems = dualAxisData.dataKey.right;

        ctx.font = FONTS.normal;

        contentHeight = drawLegendItems(ctx, [...leftItems, ...rightItems], margin, contentHeight, maxWidth);
        contentHeight += CANVAS_CONFIG.legendHeight + 20;
    } else {
        // Leyenda simple
        const legendItems = getLegendItems(
            singleAxisData?.dataKey || [],
            typeOfIndicator,
            true
        );

        ctx.font = FONTS.normal;
        contentHeight = drawLegendItems(ctx, legendItems, margin, contentHeight, maxWidth);
        contentHeight += CANVAS_CONFIG.legendHeight + 20;
    }

    return contentHeight;
};

const useDownloadChart = (
    chartRef: RefObject<any>,
    chart: Graphics,
    source: string,
    typeOfIndicator: string,
    filtersSelected: { [key: string]: string[] },
    breadcrumbs: string,
    name_indicator: string,
    dataChart: BarCharts[],
    filters: Filtro[] | null,
) => {
    const hasSecondaryAxis = !!chart?.eje_y2;

    const dualAxisData = useDataKeyDualAxis(
        hasSecondaryAxis ? dataChart : [],
        hasSecondaryAxis ? chart?.eje_y2_data?.variable_analisis?.[0] : undefined
    );

    const singleAxisData = useDataKey(
        hasSecondaryAxis ? [] : dataChart,
        hasSecondaryAxis ? [] : (filters ?? [])
    );

    const downloadChart = useCallback((format: "png" | "jpg" = "png") => {
        if (!chartRef.current) return;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const svgElement = chartRef.current.querySelector("svg");

        if (!svgElement || !ctx) return;

        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgSize = svgElement.getBoundingClientRect();

        // Preparar datos
        const dataKeyForHeight = hasSecondaryAxis
            ? [...(dualAxisData.dataKey.left ?? []), ...(dualAxisData.dataKey.right ?? [])]
            : singleAxisData?.dataKey || [];

        const maxWidth = svgSize.width + CANVAS_CONFIG.padding + CANVAS_CONFIG.margin * 2 - 40;

        // Configurar canvas
        canvas.width = svgSize.width + CANVAS_CONFIG.padding + CANVAS_CONFIG.margin * 2;
        canvas.height = calculateCanvasHeight(
            breadcrumbs,
            filtersSelected,
            dataKeyForHeight,
            typeOfIndicator,
            svgSize.height,
            ctx,
            CANVAS_CONFIG.margin,
            chart?.nota_grafico,
            CANVAS_CONFIG.legendHeight,
            source
        );

        const img = new Image();
        const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
            // Fondo blanco para JPG
            if (format === "jpg") {
                ctx.fillStyle = COLORS.white;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }

            let contentHeight = CANVAS_CONFIG.margin + 20;

            // Dibujar header (breadcrumb y título)
            contentHeight = drawHeader(
                ctx,
                breadcrumbs,
                name_indicator,
                chart.titulo,
                canvas.width,
                contentHeight
            );

            // Dibujar filtros
            contentHeight = drawFilters(
                ctx,
                filtersSelected,
                CANVAS_CONFIG.margin,
                contentHeight
            );

            // Dibujar gráfico SVG
            ctx.drawImage(img, CANVAS_CONFIG.margin, contentHeight);
            contentHeight += svgSize.height;

            // Dibujar leyenda
            contentHeight = drawLegend(
                ctx,
                hasSecondaryAxis,
                dualAxisData,
                singleAxisData,
                typeOfIndicator,
                CANVAS_CONFIG.margin,
                contentHeight,
                maxWidth
            );

            // Dibujar footer (nota y fuente)
            drawFooter(
                ctx,
                chart?.nota_grafico,
                source,
                CANVAS_CONFIG.margin,
                contentHeight,
                maxWidth
            );

            URL.revokeObjectURL(url);

            // Descargar
            const imgURL = canvas.toDataURL(`image/${format}`);
            const downloadLink = document.createElement("a");
            downloadLink.href = imgURL;
            downloadLink.download = `${chart.titulo}.${format}`;
            downloadLink.click();
        };

        sendAnalyticsEvent("downloaded_report", {
            report_title: chart.titulo.trim(),
            file_type: format,
            source: name_indicator,
            module: "Visualizador de indicadores",
        });

        img.src = url;
    }, [
        chartRef,
        chart,
        source,
        typeOfIndicator,
        filtersSelected,
        breadcrumbs,
        name_indicator,
        hasSecondaryAxis,
        dualAxisData,
        singleAxisData,
    ]);

    return { downloadChart };
};

export default useDownloadChart;